/**
 * Централізована конфігурація модульної системи перекладів.
 *
 * Один «модуль перекладу» === один файл `assets/i18n/<lang>.<module>.json`,
 * який містить набір кореневих неймспейсів (top-level ключів).
 * Базовий словник (`assets/i18n/<lang>.json`) вантажиться одразу на старті,
 * решта — ліниво, за потреби.
 */

export type Language = 'ua' | 'en';

export const LANGUAGES: readonly Language[] = ['ua', 'en'] as const;

export const DEFAULT_LANGUAGE: Language = 'ua';

/** Ключ у localStorage, під яким зберігається активна мова. */
export const LANGUAGE_STORAGE_KEY = 'language';

/** Шлях до локалей та розширення файлів. */
export const I18N_PREFIX = '/assets/i18n/';
export const I18N_SUFFIX = '.json';

/**
 * Реєстр модулів перекладу: `<модуль> -> кореневі неймспейси`, що в ньому лежать.
 *
 * Мапа використовується у двох напрямках:
 *  - module -> namespaces: щоб згенерувати/перевірити файли локалей (scripts/i18n-split.mjs);
 *  - namespace -> module: щоб `LazyMissingTranslationHandler` міг за відсутнім ключем
 *    зрозуміти, який саме файл треба довантажити.
 */
export const TRANSLATION_MODULES = {
  olx: [
    'olx',
    'buy_olx_ads_bundles',
    'buy_olx_packages',
    'advert_planner_type',
    'advertiser_type',
    'bulk_change_price',
    'planner',
    'planner_action',
    'status_table_planner',
  ],
  delivery: ['ukr_poshta_status', 'status_table_nova_poshta'],
  automation: ['automation_action', 'automation_logs', 'alias', 'alias_events', 'automation_flow'],
  support: ['support_questions', 'ticket_system'],
  channel: [
    'channels_type',
    'channel_status',
    'channel_status_new',
    'channel_types_group',
    'chat_settings',
    'chats_sidebar_menu',
    'another_crm',
    'ai_integration',
    'call_status',
  ],
  settings: [
    'permissions',
    'permissions_info',
    'permissions_types',
    'companies_info',
    'spaces',
    'space_meter',
    'custom_filters',
  ],
  auth: ['verifying_email', 'verifying_phone', 'reset_password'],
  site: ['site_statuses', 'site_builder'],
  stock: ['stock'],
  payments: ['board_payments'],
  prro: ['prro'],
  tasks: ['crm_tasks'],
  novaposhta: ['novaposhta'],
} as const satisfies Record<string, readonly string[]>;

export type TranslationModule = keyof typeof TRANSLATION_MODULES;

export const TRANSLATION_MODULE_NAMES = Object.keys(TRANSLATION_MODULES) as TranslationModule[];

/** Зворотна мапа `неймспейс -> модуль`, будується один раз на старті. */
export const NAMESPACE_TO_MODULE: ReadonlyMap<string, TranslationModule> = new Map(
  TRANSLATION_MODULE_NAMES.flatMap((module) =>
    TRANSLATION_MODULES[module].map((namespace) => [namespace, module] as const),
  ),
);

/**
 * Модулі, які вантажаться одразу після базового словника.
 * Сюди потрапляє лише те, що рендериться у глобальному лейауті (шапка, сайдбари).
 */
export const EAGER_TRANSLATION_MODULES: readonly TranslationModule[] = ['channel', 'settings'];

/**
 * Мапа `префікс роуту -> модулі`, які треба підвантажити при переході.
 * Перевіряється через `url.startsWith(prefix)`, тому порядок не важливий.
 */
export const ROUTE_TRANSLATION_MODULES: Readonly<Record<string, readonly TranslationModule[]>> = {
  '/crm/v2/prro': ['prro', 'payments'],
  '/crm/market': ['prro', 'payments'],
  '/auth': ['auth'],
  '/login': ['auth'],
  '/registration': ['auth'],
  '/crm/olx': ['olx', 'delivery'],
  '/crm/power-olx': ['olx'],
  '/crm/automation': ['automation'],
  '/crm/support': ['support'],
  '/crm/channel': ['channel'],
  '/crm/integration': ['channel'],
  '/crm/settings': ['settings'],
  '/crm/team': ['settings'],
  '/crm/site': ['site'],
  '/crm/application/sites': ['site'],
  '/crm/warehouse': ['stock'],
  '/crm/v2': ['stock', 'payments', 'novaposhta'],
  '/crm/marketplace': ['novaposhta'],
  '/crm/tasks': ['tasks'],
  '/crm/proposals': ['delivery'],
};

/** Ім'я файлу локалі для мови + (опційно) модуля. */
export function translationFileName(language: string, module?: TranslationModule | null): string {
  return module ? `${language}.${module}${I18N_SUFFIX}` : `${language}${I18N_SUFFIX}`;
}

/** Ім'я модуля за повним ключем перекладу (`olx.title` -> `olx`). */
export function moduleForKey(key: string): TranslationModule | null {
  const namespace = key.split('.')[0];
  return NAMESPACE_TO_MODULE.get(namespace) ?? null;
}

/** Нормалізує довільне значення до підтримуваної мови. */
export function normalizeLanguage(value: string | null | undefined): Language {
  const short = (value ?? '').toLowerCase().split('-')[0];
  return (LANGUAGES as readonly string[]).includes(short) ? (short as Language) : DEFAULT_LANGUAGE;
}
